/** @jsxImportSource @emotion/react */

import { useMutation } from '@apollo/client'
import { Modal } from '@mui/material'
import Typography from '@mui/material/Typography'
import { Dispatch, FC, SetStateAction, useCallback, useEffect, useState } from 'react'
import last from 'lodash/last'
import { ClassroomTabEnum } from '__generatedTypes__/globalTypes'
import {
  bulkRemoveFlashCardGroup,
  bulkRemoveFlashCardGroupVariables,
} from '__generatedTypes__/bulkRemoveFlashCardGroup'
import {
  bulkUpdateFlashCardGroup,
  bulkUpdateFlashCardGroupVariables,
} from '__generatedTypes__/bulkUpdateFlashCardGroup'
import { findAllFlashCardGroup_findAllFlashCardGroup_items as IGroup } from '__generatedTypes__/findAllFlashCardGroup'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import DeleteModal from 'components/Modals/DeleteModal'
import Table, { TableProps } from 'components/Table'
import TableButtons from 'components/TableButtons/TableButtons'
import { ClassroomTabLiveSwitcher } from 'components/ClassroomTabLiveSwitcher'
import client from 'configs/apolloClient'
import {
  BULK_REMOVE_FLASH_CARD_GROUP,
  BULK_UPDATE_FLASH_CARD_GROUP,
  FIND_ALL_FLASH_CARD_GROUP,
} from 'queries/flashCardsGroup'
import { toast } from 'utils/toast'
import { SubjectExtendedFull } from 'hooks/usePlatformsAndSubjects'
import * as commonClasses from 'components/shared/GroupCards/commonStyles'
import { GROUP_COLORS } from '../consts'
import ManageFlashCardsGroupModal from './ManageFlashCardsGroupModal'
import * as classes from './styles'

interface IProps {
  groups: IGroup[]
  setGroups: Dispatch<SetStateAction<IGroup[]>>
  selectedSubject: SubjectExtendedFull
}

const FlashCardsGroups: FC<IProps> = ({ setGroups, groups, selectedSubject }) => {
  const [isGroupModalOpen, setGroupModalOpen] = useState<boolean>(false)
  const [isBulkDeleteModalOpen, setBulkDeleteModalOpen] = useState<boolean>(false)
  const [selectedGroup, setSelectedGroup] = useState<IGroup | null>(null)
  const [availableGroupColors, setAvailableGroupColors] = useState<string[]>([])
  const [deleteGroup, setDeleteGroup] = useState<IGroup[]>([])

  const [bulkUpdateFlashCardGroup] = useMutation<bulkUpdateFlashCardGroup, bulkUpdateFlashCardGroupVariables>(
    BULK_UPDATE_FLASH_CARD_GROUP
  )
  const [bulkRemoveFlashCardGroup] = useMutation<bulkRemoveFlashCardGroup, bulkRemoveFlashCardGroupVariables>(
    BULK_REMOVE_FLASH_CARD_GROUP
  )

  const handleBulkDelete = useCallback(async () => {
    try {
      const ids = deleteGroup.map((group) => ({ id: group.id }))
      await bulkRemoveFlashCardGroup({ variables: { ids } })
      setDeleteGroup([])
      client.refetchQueries({
        include: [FIND_ALL_FLASH_CARD_GROUP],
      })
      setBulkDeleteModalOpen(false)

      toast.success('Groups deleted successfully')
    } catch (error) {
      toast.error('Failed to delete groups')
    }
  }, [deleteGroup])

  const handleEditClick = (group: IGroup) => {
    setSelectedGroup(group)
    setGroupModalOpen(true)
    setDeleteGroup([])
  }

  const handleModalClose = () => {
    setSelectedGroup(null)
    setGroupModalOpen(false)
  }

  useEffect(() => {
    const groupColors = groups.map((group) => group.color)
    setAvailableGroupColors(GROUP_COLORS.filter((color) => !groupColors.includes(color)))
  }, [groups, selectedSubject.id])

  useEffect(() => {
    setDeleteGroup([])
  }, [selectedSubject.id])

  const GroupsColumns: TableProps<IGroup>['columns'] = [
    {
      title: 'Group Title',
      renderCell: (group) => (
        <div css={classes.groupTitle}>
          <div css={classes.circle(group.color)} />
          <Typography css={commonClasses.longText} variant="body2" data-testid="group-title">
            {group.title}
          </Typography>
        </div>
      ),
      flex: '0 1 484px',
    },
    {
      title: 'Description',
      renderCell: (group) => (
        <Typography css={commonClasses.longText} variant="body2">
          {group.description}
        </Typography>
      ),
      flex: '0 1 484px',
    },
    {
      renderCell: (group) => (
        <EditIcon css={commonClasses.editIcon} onClick={() => handleEditClick(group)} data-testid="edit-group-btn" />
      ),
      flex: '0 1 29px',
    },
  ]

  const onSort = useCallback(
    async (items: IGroup[]) => {
      try {
        if (items.length > 0) {
          setGroups(items)

          await bulkUpdateFlashCardGroup({
            variables: {
              bulkUpdateFlashCardGroupInput: items.map(({ id }, order) => ({ id, order })),
            },
          })
          toast.success('Groups order updated successfully')
        }
      } catch (error) {
        toast.error('Failed to update groups order')
      }
    },
    [setGroups]
  )

  const lastGroupOrder = last(groups)?.order

  return (
    <div>
      <div css={classes.header}>
        <Typography css={classes.title} variant="title3" component="p">
          Group
        </Typography>
        <ClassroomTabLiveSwitcher selectedSubject={selectedSubject} tabEnum={ClassroomTabEnum.FlashCards} />
      </div>
      <Table
        columns={GroupsColumns}
        items={groups}
        isSelectable
        selectedItems={deleteGroup}
        onSelect={setDeleteGroup}
        onSort={onSort}
        isSortable
      />
      <TableButtons
        itemName="Group"
        areItemsToDelete={deleteGroup.length !== 0}
        onAddItem={() => setGroupModalOpen(true)}
        onCancel={() => setDeleteGroup([])}
        onBatchDelete={() => setBulkDeleteModalOpen(true)}
      />
      <Modal open={isGroupModalOpen} onClose={handleModalClose}>
        <ManageFlashCardsGroupModal
          newGroupOrder={lastGroupOrder !== undefined ? lastGroupOrder + 1 : 0}
          availableGroupColors={availableGroupColors}
          handleClose={handleModalClose}
          group={selectedGroup}
          selectedSubjectId={selectedSubject.id}
        />
      </Modal>
      <DeleteModal
        open={isBulkDeleteModalOpen}
        count={deleteGroup.length}
        onClose={() => setBulkDeleteModalOpen(false)}
        onConfirm={handleBulkDelete}
      />
    </div>
  )
}

export default FlashCardsGroups
